"use client"

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { MessageSquare, X } from "lucide-react"
import type { FeedbackThread } from "@/lib/feedback/types"
import {
  buildNormalizedIndex,
  buildRangeForMatch,
  findQuoteMatches,
} from "./anchorThreads"
import {
  FEEDBACK_REACTION_GLYPHS,
  FEEDBACK_REACTION_LABELS,
  parseReactionFromBody,
} from "./feedbackReactions"

// --- Editable copy / sizing (cmd-click any to change them) ---

// Title shown at the top of the read-only popover.
const SUBMITTED_POPOVER_TITLE = "Feedback you submitted"

// Label of the (always-enabled) dismiss button.
const SUBMITTED_CLOSE_LABEL = "Close"

// Horizontal gap (px) from body-text column's right edge to icons.
const ICON_MARGIN_GAP = 16

// Minimum vertical gap (px) between stacked icons, no overlap.
const ICON_STACK_GAP = 6

const ICON_SIZE = 24

const POPOVER_WIDTH = 320
const POPOVER_VIEWPORT_MARGIN = 12
const POPOVER_ANCHOR_GAP = 12

// How long the pointer may dwell between icon and popover (or briefly
// stray off either) before the hover-opened popover closes.
const HOVER_CLOSE_GRACE_MS = 200

// Chars of quoted text shown in the popover preview before
// truncating with an ellipsis (matches the create dialog).
const MAX_SELECTION_PREVIEW_LENGTH = 45

// ------------------------------------------------------------

const HIGHLIGHT_NAME = "ai2030-feedback-submitted"

// Solid underline for the passage whose icon/popover is hovered.
const ACTIVE_HIGHLIGHT_NAME = "ai2030-feedback-submitted-active"

// Prose columns we anchor into. Supplement essays render one
// .supplement-essay-prose block; the Plan A playbook renders several
// .primary-branch-prose blocks (one per section). Avoid the bare
// .markdown-content selector because footnote tooltips reuse it.
const PROSE_SELECTOR =
  ".markdown-content.supplement-essay-prose, .markdown-content.primary-branch-prose"

// Monospace stack for the popover chrome — matches the create
// dialog's typography. (Serif for the quoted note; see below.)
const DIALOG_UI_FONT_FAMILY =
  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, ' +
  '"Liberation Mono", "Courier New", monospace'

// Serif stack for the quoted-text preview (matches the report body
// copy and the create dialog's preview).
const DIALOG_COPY_FONT_FAMILY =
  'et-book, Palatino, "Palatino Linotype", "Palatino LT STD", ' +
  '"Book Antiqua", Georgia, serif'

const getSelectionPreview = (text: string) => {
  const compact = text.replace(/\s+/g, " ").trim()
  if (compact.length <= MAX_SELECTION_PREVIEW_LENGTH) {
    return { text: compact, truncated: false }
  }

  return {
    text: compact.slice(0, MAX_SELECTION_PREVIEW_LENGTH).trimEnd(),
    truncated: true,
  }
}

type SubmittedFeedbackLayerProps = {
  // The current reader's own feedback threads for this page.
  threads: FeedbackThread[]
  enabled: boolean
  // Bump to force a re-measure when the rendered prose changes (e.g.
  // a version swap or route change) so anchors track fresh content.
  recomputeKey?: string | number
}

type Anchor = {
  thread: FeedbackThread
  // Document (page) coordinates of the highlighted passage.
  topPage: number
  bottomPage: number
  centerPage: number
  // Right edge (page coords) of the prose column this match lives in,
  // so the margin icon sits just outside the column that rendered it.
  rightPage: number
}

const supportsCustomHighlights = () =>
  typeof CSS !== "undefined" &&
  "highlights" in CSS &&
  typeof Highlight !== "undefined"

const clearHighlight = () => {
  if (!supportsCustomHighlights()) return
  CSS.highlights.delete(HIGHLIGHT_NAME)
  CSS.highlights.delete(ACTIVE_HIGHLIGHT_NAME)
}

const getTimeLabel = (value: string) =>
  new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value))

type OpenPopover = {
  thread: FeedbackThread
  anchorTopViewport: number
  anchorBottomViewport: number
  leftViewport: number
}

export const SubmittedFeedbackLayer = ({
  threads,
  enabled,
  recomputeKey,
}: SubmittedFeedbackLayerProps) => {
  const [mounted, setMounted] = useState(false)
  const [anchors, setAnchors] = useState<Anchor[]>([])
  const [open, setOpen] = useState<OpenPopover | null>(null)
  const [popoverTop, setPopoverTop] = useState<number | null>(null)
  const popoverRef = useRef<HTMLDivElement | null>(null)
  // Live ranges per thread, so the hovered/open passage gets its
  // solid underline without re-matching the DOM.
  const rangesByThreadRef = useRef(new Map<string, Range>())
  const hoverCloseTimeoutRef = useRef<number | null>(null)

  useEffect(() => setMounted(true), [])

  const cancelScheduledClose = useCallback(() => {
    if (hoverCloseTimeoutRef.current !== null) {
      window.clearTimeout(hoverCloseTimeoutRef.current)
      hoverCloseTimeoutRef.current = null
    }
  }, [])

  // Close shortly after the pointer leaves both icon and popover, so
  // there's a grace period to travel between them.
  const scheduleClose = useCallback(() => {
    cancelScheduledClose()
    hoverCloseTimeoutRef.current = window.setTimeout(() => {
      hoverCloseTimeoutRef.current = null
      setOpen(null)
    }, HOVER_CLOSE_GRACE_MS)
  }, [cancelScheduledClose])

  useEffect(() => cancelScheduledClose, [cancelScheduledClose])

  const recompute = useCallback(() => {
    if (!enabled || threads.length === 0) {
      clearHighlight()
      setAnchors([])
      return
    }

    const proseContainers = Array.from(
      document.querySelectorAll<HTMLElement>(PROSE_SELECTOR)
    )
    if (proseContainers.length === 0) {
      clearHighlight()
      setAnchors([])
      return
    }

    const scrollX = window.scrollX
    const scrollY = window.scrollY
    const nextAnchors: Anchor[] = []
    const ranges: Range[] = []
    const nextRangesByThread = new Map<string, Range>()
    // A quote can appear in multiple blocks (e.g. repeated phrasing);
    // anchor each thread once, in the first block it's found.
    const matchedThreadIds = new Set<string>()

    for (const prose of proseContainers) {
      const remaining = threads.filter(t => !matchedThreadIds.has(t.id))
      if (remaining.length === 0) break

      const { normalized, entries } = buildNormalizedIndex(prose)
      if (entries.length === 0) continue

      const matches = findQuoteMatches(remaining, normalized)
      if (matches.length === 0) continue

      const proseRight = prose.getBoundingClientRect().right + scrollX
      for (const match of matches) {
        const range = buildRangeForMatch(entries, match)
        if (!range) continue
        const rect = range.getBoundingClientRect()
        if (rect.width === 0 && rect.height === 0) continue
        ranges.push(range)
        nextRangesByThread.set(match.thread.id, range)
        const topPage = rect.top + scrollY
        const bottomPage = rect.bottom + scrollY
        matchedThreadIds.add(match.thread.id)
        nextAnchors.push({
          thread: match.thread,
          topPage,
          bottomPage,
          centerPage: (topPage + bottomPage) / 2,
          rightPage: proseRight,
        })
      }
    }

    if (supportsCustomHighlights()) {
      if (ranges.length > 0) {
        CSS.highlights.set(HIGHLIGHT_NAME, new Highlight(...ranges))
      } else {
        clearHighlight()
      }
    }

    nextAnchors.sort((a, b) => a.centerPage - b.centerPage)
    rangesByThreadRef.current = nextRangesByThread
    setAnchors(nextAnchors)
  }, [enabled, threads])

  useLayoutEffect(() => {
    recompute()

    const resizeObserver = new ResizeObserver(() => recompute())
    for (const prose of Array.from(
      document.querySelectorAll<HTMLElement>(PROSE_SELECTOR)
    )) {
      resizeObserver.observe(prose)
    }
    window.addEventListener("resize", recompute)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener("resize", recompute)
      clearHighlight()
    }
  }, [recompute, recomputeKey])

  // Stagger overlapping icons, keeping them near their anchor.
  const stackedIcons = useMemo(() => {
    let lastBottom = -Infinity
    return anchors.map(anchor => {
      const desiredTop = anchor.centerPage - ICON_SIZE / 2
      const top = Math.max(desiredTop, lastBottom + ICON_STACK_GAP)
      lastBottom = top + ICON_SIZE
      return { anchor, top }
    })
  }, [anchors])

  const openForAnchor = useCallback((anchor: Anchor) => {
    const leftViewport = Math.min(
      Math.max(
        anchor.rightPage - window.scrollX + ICON_MARGIN_GAP + ICON_SIZE + POPOVER_ANCHOR_GAP,
        POPOVER_VIEWPORT_MARGIN
      ),
      window.innerWidth - POPOVER_WIDTH - POPOVER_VIEWPORT_MARGIN
    )
    setOpen({
      thread: anchor.thread,
      anchorTopViewport: anchor.topPage - window.scrollY,
      anchorBottomViewport: anchor.bottomPage - window.scrollY,
      leftViewport,
    })
  }, [])

  // Solid-underline the passage whose popover is open.
  useEffect(() => {
    if (!supportsCustomHighlights()) return

    const range = open ? rangesByThreadRef.current.get(open.thread.id) : null
    if (range) {
      CSS.highlights.set(ACTIVE_HIGHLIGHT_NAME, new Highlight(range))
    } else {
      CSS.highlights.delete(ACTIVE_HIGHLIGHT_NAME)
    }
  }, [open])

  // Center the popover on its highlight, clamped to the viewport.
  useLayoutEffect(() => {
    if (!open) {
      setPopoverTop(null)
      return
    }
    const element = popoverRef.current
    if (!element) return

    const height = element.offsetHeight
    const center = (open.anchorTopViewport + open.anchorBottomViewport) / 2
    const maxTop = window.innerHeight - height - POPOVER_VIEWPORT_MARGIN
    const centeredTop = center - height / 2
    setPopoverTop(
      maxTop < POPOVER_VIEWPORT_MARGIN
        ? POPOVER_VIEWPORT_MARGIN
        : Math.min(Math.max(centeredTop, POPOVER_VIEWPORT_MARGIN), maxTop)
    )
  }, [open])

  // Dismiss the popover on Escape, scroll, or an outside click.
  useEffect(() => {
    if (!open) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(null)
    }
    const onScroll = () => setOpen(null)
    const onPointerDown = (event: MouseEvent) => {
      if (!popoverRef.current?.contains(event.target as Node)) setOpen(null)
    }

    window.addEventListener("keydown", onKeyDown)
    window.addEventListener("scroll", onScroll, true)
    window.addEventListener("mousedown", onPointerDown)
    return () => {
      window.removeEventListener("keydown", onKeyDown)
      window.removeEventListener("scroll", onScroll, true)
      window.removeEventListener("mousedown", onPointerDown)
    }
  }, [open])

  if (!mounted || !enabled) return null

  const openPreview = open?.thread.selectedText
    ? getSelectionPreview(open.thread.selectedText)
    : null

  return createPortal(
    <div
      className="pointer-events-none absolute left-0 top-0 z-[1150] hidden md:block"
      aria-hidden={false}
    >
      {stackedIcons.map(({ anchor, top }) => (
        <button
          key={anchor.thread.id}
          type="button"
          aria-label="View feedback you submitted"
          onMouseDown={event => event.stopPropagation()}
          onMouseEnter={() => {
            cancelScheduledClose()
            openForAnchor(anchor)
          }}
          onMouseLeave={scheduleClose}
          onClick={() => {
            cancelScheduledClose()
            openForAnchor(anchor)
          }}
          className="ai2030-feedback-icon-pop pointer-events-auto absolute grid
            place-items-center rounded-[4px] text-black/45 transition
            hover:bg-black/5 hover:text-black/80"
          style={{
            top: `${top}px`,
            left: `${anchor.rightPage + ICON_MARGIN_GAP}px`,
            height: `${ICON_SIZE}px`,
            width: `${ICON_SIZE}px`,
          }}
        >
          <MessageSquare size={16} strokeWidth={1.8} />
        </button>
      ))}

      {open && (
        <div
          ref={popoverRef}
          onMouseDown={event => event.stopPropagation()}
          onMouseEnter={cancelScheduledClose}
          onMouseLeave={scheduleClose}
          className="pointer-events-auto fixed overflow-hidden rounded-[6px] border
            border-black bg-[#fffff8] text-black shadow-[0_18px_48px_rgba(0,0,0,0.18)]"
          style={{
            left: `${open.leftViewport}px`,
            top: `${popoverTop ?? open.anchorTopViewport}px`,
            width: `${POPOVER_WIDTH}px`,
            fontFamily: DIALOG_UI_FONT_FAMILY,
          }}
        >
          <div className="flex items-center justify-between gap-2 bg-black px-4 py-2.5
            text-white"
          >
            <span className="text-sm font-semibold leading-none">
              {SUBMITTED_POPOVER_TITLE}
            </span>
            <button
              type="button"
              aria-label="Close"
              onClick={() => setOpen(null)}
              className="grid h-6 w-6 shrink-0 place-items-center rounded-[4px]
                text-white transition hover:bg-white/15"
            >
              <X size={14} strokeWidth={2.2} />
            </button>
          </div>

          <div className="px-4 pb-4 pt-3">
            {openPreview && (
              <p
                className="m-0 mb-3 truncate text-[15px] leading-snug text-black
                  opacity-60"
                style={{ fontFamily: DIALOG_COPY_FONT_FAMILY }}
              >
                <span className="box-decoration-clone rounded-[2px] bg-[#accef7]
                  px-0.5 text-black"
                >
                  {openPreview.text}
                  {openPreview.truncated && (
                    <span className="text-black/40"> …</span>
                  )}
                </span>
              </p>
            )}

            <div className="space-y-3">
              {open.thread.comments.map(comment => {
                const { reaction, note } = parseReactionFromBody(comment.body)
                return (
                  <div key={comment.id} className="text-sm leading-snug">
                    <div className="flex items-center gap-1 text-[11px] text-black/45">
                      <span className="font-semibold text-black/65">
                        {comment.authorName ?? "Unknown"}
                      </span>
                      <span>·</span>
                      <span>{getTimeLabel(comment.createdAt)}</span>
                    </div>

                    {reaction && (
                      <div
                        className="mt-1.5 text-base leading-none"
                        title={FEEDBACK_REACTION_LABELS[reaction]}
                      >
                        {FEEDBACK_REACTION_GLYPHS[reaction]}
                      </div>
                    )}

                    {note && (
                      <div className="mt-1.5 whitespace-pre-wrap text-black/80">
                        {note}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            <button
              type="button"
              onClick={() => setOpen(null)}
              className="mt-3 flex h-11 w-full items-center justify-center rounded-[4px]
                border-2 border-black bg-[#fffff8] text-sm font-semibold text-black
                transition hover:bg-black/5"
            >
              {SUBMITTED_CLOSE_LABEL}
            </button>
          </div>
        </div>
      )}
    </div>,
    document.body
  )
}
